import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { translatedMessage } from '../../service/LanguageService';
import { CompetitionService } from '../../service/CompetitionService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SecurityService from '../../service/SecurityService';
import { Divider } from 'primereact/divider';
import GeneralUtils from '../../utilities/GeneralUtils';
import CompetitionJobCalendarEditDialog from '../../pages/competition/CompetitionJobCalendarEditDialog';
import ValidationErrorModalComponent from '../ValidationErrorModalComponent';
import { DataTable } from 'primereact/datatable';
import DataTableUtils from '../../utilities/DataTableUtils';
import { Column } from 'primereact/column';
import CompetitionUtils from '../../utilities/CompetitionUtils';
import DeleteModalComponent from '../DeleteModalComponent';
import CompetitionJobExamEditDialog from '../../pages/competition/CompetitionJobExamEditDialog';
import CompetitionJobWrittenExamEditDialog from '../../pages/competition/CompetitionJobWrittenExamEditDialog';
import { hasPermission } from '../../service/UserService';
import CompetitionJobWrittenExamApproveDialog from '../../pages/competition/CompetitionJobWrittenExamApproveDialog';
import { Message } from 'primereact/message';
import CompetitionJobWrittenExamChangeHistoryDialog from '../../pages/competition/CompetitionJobWrittenExamChangeHistoryDialog';

const CompetitionJobCalendarComponent = (props) => {
    const [competition, setCompetition] = useState(null);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [hasEditPermission, setHasEditPermission] = useState(false);
    const [calendarList, setCalendarList] = useState([]);
    const [deleteExamDialogVisible, setDeleteExamDialogVisible] = useState(false);
    const [editExamDialogVisible, setEditExamDialogVisible] = useState(false);
    const [selectedExam, setSelectedExam] = useState(null);
    const [canEditWrittenTest, setCanEditWrittenTest] = useState(false);
    const [writtenTestDialogVisible, setWrittenTestDialogVisible] = useState(false);
    const [writtenTestApproveDialogVisible, setWrittenTestApproveDialogVisible] = useState(false);
    const [writtenTestHistoryDialogVisible, setWrittenTestHistoryDialogVisible] = useState(false);
    const [calendarChange, setCalendarChange] = useState(null);
    const [hasCalendarChange, setHasCalendarChange] = useState(false);
    const [hasCalendarChangeInProgress, setHasCalendarChangeInProgress] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [interviewExamDelay, setInterviewExamDelay] = useState(8);

    const toast = useRef(null);
    const dt = useRef(null);

    const competitionService = useMemo(() => new CompetitionService(), []);

    const { competitionIdParam } = useParams();

    useEffect(() => {
        setIsLoading(true);

        const checkEditPermission = async () => {
            setHasEditPermission(await SecurityService.check('CAN_EDIT_JOB_COMPETITION', competitionIdParam));
        };

        const canEditWrittenTest = async () => {
            setCanEditWrittenTest(await SecurityService.check('CAN_EDIT_JOB_COMPETITION_WRITTEN_TEST', competitionIdParam));
        };

        const getCompetition = async () => {
            setCompetition(props?.competition);
            if (props?.competition.positionCategory.includes('PROMOVARE')) {
                setInterviewExamDelay(5)
            } else {
                setInterviewExamDelay(8)
            }

            await checkEditPermission()
            await canEditWrittenTest()
            await getCalendar()
        };

        getCompetition();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [competitionIdParam]);

    const getCalendar = async () => {
        await competitionService.getCompetitionCalendar(competitionIdParam)
            .then((_calendarList) => {
                // console.log("calendarList: ", _calendarList);
                setCalendarList(_calendarList);
                let _writtenTest = _calendarList.filter(item => item.examType === "WRITTEN_EXAM")[0];
                // console.log("writtenTest: ", _writtenTest)
                setCompetition(prev => ({
                    ...prev,
                    writtenTestDate: _writtenTest?.examDate ? new Date(Date.parse(_writtenTest?.examDate)) : '',
                    writtenTestLocation: _writtenTest?.examLocation ? _writtenTest?.examLocation : '',
                }))
                let _hasCalendarChange = _writtenTest && _writtenTest.competitionCalendarChanges !== null && _writtenTest.competitionCalendarChanges.length > 0;
                // console.log("hasCalendarChange: ", _hasCalendarChange);                

                if (_hasCalendarChange) {
                    let _hasCalendarChangeInProgress = _writtenTest.competitionCalendarChanges.filter(item => item.approved === null).length > 0;
                    // console.log("hasCalendarChangeInProgress: ", _hasCalendarChangeInProgress);
                    setHasCalendarChangeInProgress(_hasCalendarChangeInProgress)
                }

                setHasCalendarChange(_hasCalendarChange);
                setIsLoading(false);
            })
            .catch((error) => error && toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    const afterSave = (savedCompetition, success, error) => {
        if (success) {
            // console.log("savedCompetition: ", savedCompetition);
            setCompetition(savedCompetition)
            getCalendar()
            toast?.current?.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
        } else {
            if (error?.hasValidationError) {
                setValidationErrors(error.errorCodes)
                setShowValidationErrorDialog(true)
            } else {
                error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
            }
        }
    };

    const afterExamSave = (savedCalendar, success, error) => {
        if (success) {
            // console.log("savedCalendar: ", savedCalendar);
            getCalendar()
            toast?.current?.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
        } else {
            if (error?.hasValidationError) {
                setValidationErrors(error.errorCodes)
                setShowValidationErrorDialog(true)
            } else {
                error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
            }
        }
    };

    const afterChangeApprove = (success, error) => {
        if (success) {
            getCalendar()
            toast?.current?.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
        } else {
            if (error?.hasValidationError) {
                setValidationErrors(error.errorCodes)
                setShowValidationErrorDialog(true)
            } else {
                error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
            }
        }
    };

    const editWrittenTest = (_exam) => {
        setCalendarChange({ competitionCalendar: { id: _exam.id }, examDate: _exam.examDate, examLocation: _exam.examLocation });
        setWrittenTestDialogVisible(true);
    };

    const showNewDialog = () => {
        setSelectedExam(null);
        setEditExamDialogVisible(true);
    };

    const showEditDialog = (_exam) => {
        setSelectedExam(_exam);
        setEditExamDialogVisible(true);
    };

    const showDeleteDialog = (_exam) => {
        setSelectedExam(_exam);
        setDeleteExamDialogVisible(true);
    };

    const deleteExam = () => {
        competitionService.deleteCompetitionCalendar(selectedExam.id)
            .then(() => {
                getCalendar()
                toast.current.show({ severity: 'success', summary: translatedMessage('generic.delete.success') });
            })
            .catch((error) => {
                toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            })
            .finally(() => {
                setDeleteExamDialogVisible(false);
                setSelectedExam(null);
            });
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                {canEditWrittenTest && rowData.examType === "WRITTEN_EXAM" && <>
                    <Button
                        icon="pi pi-clock"
                        className="p-button-rounded p-button-info m-1"
                        onClick={() => editWrittenTest(rowData)}
                        tooltip={translatedMessage('competition.writtenTest.postpone')}
                        tooltipOptions={{ position: 'top' }}
                        disabled={hasCalendarChangeInProgress}
                    />
                    <Button
                        icon="pi pi-history"
                        className="p-button-rounded p-button-info m-1"
                        onClick={() => setWrittenTestHistoryDialogVisible(true)}
                        tooltip={translatedMessage('competition.writtenTest.postpone.history')}
                        tooltipOptions={{ position: 'top' }}
                        style={{ display: hasCalendarChange ? 'inline-flex' : 'none' }}
                    />
                </>}
                {hasEditPermission && (
                    <>
                        <Button
                            icon="pi pi-pencil"
                            className="p-button-rounded p-button-info m-1"
                            onClick={() => showEditDialog(rowData)}
                            tooltip={translatedMessage('generic.edit')}
                            tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                            disabled={rowData.examType === "WRITTEN_EXAM" || !CompetitionUtils.canEdit(competition)}
                        />
                        <Button
                            icon="pi pi-trash"
                            className="p-button-rounded p-button-danger m-1"
                            onClick={() => showDeleteDialog(rowData)}
                            tooltip={translatedMessage('generic.delete')}
                            tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                            disabled={rowData.examType === "WRITTEN_EXAM" || !CompetitionUtils.canEdit(competition)}
                        />
                    </>
                )}
            </div>
        );
    };

    const onFileUploadSuccess = (response) => {
        getCalendar()
        toast?.current?.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
    };

    const onFileUploadError = (error) => {
        if (error?.hasValidationError) {
            setValidationErrors(error.errorCodes)
            setShowValidationErrorDialog(true)
        } else {
            error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
        }

    };

    return (
        <>
            <Toast ref={toast} />

            <div className='w-full grid pcn-data-view-panel'>
                <div className='col-12 pt-0'>
                    <div className='mt-3'>
                        <Button
                            className="pcn-button-slim-with-icon p-button mr-2"
                            onClick={() => setEditDialogVisible(true)}
                            visible={hasEditPermission}
                            disabled={!CompetitionUtils.canEditCalendar(competition)}
                        >
                            <div className='flex align-items-center'>
                                <FontAwesomeIcon icon='fa-solid fa-pencil' className="mr-1 " />
                                <span>{translatedMessage('generic.edit')}</span>
                            </div>
                        </Button>
                        <Button
                            className="pcn-button-slim-with-icon p-button p-button-success mr-2"
                            onClick={() => setWrittenTestApproveDialogVisible(true)}
                            visible={canEditWrittenTest && hasPermission('COMPETITION_JOB_EDIT') && hasCalendarChange && hasCalendarChangeInProgress}
                        >
                            <div className='flex align-items-center'>
                                <FontAwesomeIcon icon='fa-regular fa-calendar-check' className="mr-1 " />
                                <span>{translatedMessage('competition.writtenTest.postpone.approve.title')}</span>
                            </div>
                        </Button>
                    </div>
                    <Divider />
                    <h5 className='mb-1 font-weight-normal'>{translatedMessage("competition.calendar.title")}</h5>
                </div>
                <div className='col-12'>
                    <div className='filed-label'>{translatedMessage('competition.job.startDate')}</div>
                    <div className='filed-value'>{GeneralUtils.formatDate(competition?.startDate)}</div>
                </div>
                <div className="col-12"><div className='mt-2 mb-1 font-bold'>{translatedMessage("competition.eligibility")}</div></div>
                <div className='col-12 md:col-6'>
                    <div className='filed-label'>{translatedMessage('competition.anfpNotificationDate')}</div>
                    <div className='filed-value'>{GeneralUtils.formatDate(competition?.anfpNotificationDate)}</div>
                </div>
                <div className='col-12 md:col-6'>
                    <div className='filed-label'>{translatedMessage('competition.job.submitEndDate')}</div>
                    <div className='filed-value'>{GeneralUtils.formatDate(competition?.submitEndDate)}</div>
                </div>
                <div className='col-12'>
                    <div className='filed-label'>{translatedMessage('competition.eligibilityEndDate')}</div>
                    <div className='filed-value'>{GeneralUtils.formatDate(competition?.eligibilityEndDate)}</div>
                </div>
                <div className='col-12 md:col-6'>
                    <div className='filed-label'>{translatedMessage('competition.eligibilityClarificationEndDate')}</div>
                    <div className='filed-value'>{GeneralUtils.formatDate(competition?.eligibilityClarificationEndDate)}</div>
                </div>
                <div className='col-12 md:col-6'>
                    <div className='filed-label'>{translatedMessage('competition.eligibilityClarificationResponseEndDate')}</div>
                    <div className='filed-value'>{GeneralUtils.formatDate(competition?.eligibilityClarificationResponseEndDate)}</div>
                </div>
                <div className='col-12 md:col-6'>
                    <div className='filed-label'>{translatedMessage('competition.eligibilityContestationEndDate')}</div>
                    <div className='filed-value'>{GeneralUtils.formatDate(competition?.eligibilityContestationEndDate)}</div>
                </div>
                <div className='col-12 md:col-6'>
                    <div className='filed-label'>{translatedMessage('competition.eligibilityContestationResponseEndDate')}</div>
                    <div className='filed-value'>{GeneralUtils.formatDate(competition?.eligibilityContestationResponseEndDate)}</div>
                </div>

                <div className='col-12 mb-3'>
                    <div className='flex flex-row align-items-center mb-2'>
                        <div className='font-bold'>{translatedMessage('competition.exams')}</div>
                        <Button
                            label={translatedMessage('competition.exam.new')}
                            icon="pi pi-plus"
                            className="pcn-button-slim-with-icon p-button-primary ml-2"
                            onClick={showNewDialog}
                            visible={hasEditPermission}
                            disabled={!CompetitionUtils.canEdit(competition) || !competition.writtenTestDate}
                        />
                    </div>
                    {!isLoading && !competition?.writtenTestDate &&
                        <Message severity="warn" text={translatedMessage('competition.writtenTest.missing')} className='mb-2' />
                    }
                    <div className='mb-2'>{translatedMessage('competition.exams.info')}</div>
                    <div className='mb-2'>{translatedMessage('competition.exam.interview.info', interviewExamDelay)}</div>
                    <DataTable
                        ref={dt}
                        value={calendarList}
                        dataKey="id"
                        paginator
                        rows={DataTableUtils.defalRowsPerPage()}
                        rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                        className="datatable-responsive pcn-datatable"
                        responsiveLayout="scroll"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate={"{first} - {last} " + translatedMessage('generic.of') + " {totalRecords}"}
                        emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                        sortField='examDate'
                        sortOrder={1}
                    >
                        <Column
                            field="examType"
                            header={translatedMessage('competition.exam.name')}
                            sortable
                            headerStyle={{ width: '15%', minWidth: '10rem' }}
                            body={(e) => CompetitionUtils.getExamNameByType(e)}
                        />
                        <Column
                            field="examDate"
                            header={translatedMessage('competition.exam.examDate')}
                            sortable
                            headerStyle={{ width: '10%', minWidth: '10rem' }}
                            body={(e) => GeneralUtils.formatDateTime(e.examDate)}
                        />
                        <Column
                            field="resultsDate"
                            header={translatedMessage('competition.exam.resultsDate')}
                            sortable
                            headerStyle={{ width: '10%', minWidth: '10rem' }}
                            body={(e) => GeneralUtils.formatDate(e.resultsDate)}
                        />
                        <Column
                            field="contestationDate"
                            header={translatedMessage('competition.exam.contestationDate')}
                            sortable
                            headerStyle={{ width: '10%', minWidth: '10rem' }}
                            body={(e) => GeneralUtils.formatDate(e.contestationDate)}
                        />
                        <Column
                            field="contestationValidationDate"
                            header={translatedMessage('competition.exam.contestationValidationDate')}
                            sortable
                            headerStyle={{ width: '10%', minWidth: '10rem' }}
                            body={(e) => GeneralUtils.formatDate(e.contestationValidationDate)}
                        />
                        <Column
                            field="examLocation"
                            header={translatedMessage('competition.exam.examLocation')}
                            sortable
                            headerStyle={{ width: '25%', minWidth: '10rem' }}
                        />
                        <Column body={actionBodyTemplate} />
                    </DataTable>
                </div>
            </div>

            {editDialogVisible &&
                <CompetitionJobCalendarEditDialog
                    value={competition}
                    visible={editDialogVisible}
                    afterSave={afterSave}
                    visibleSetter={() => setEditDialogVisible(false)}
                />
            }

            {editExamDialogVisible &&
                <CompetitionJobExamEditDialog
                    value={selectedExam}
                    competition={competition}
                    visible={editExamDialogVisible}
                    afterSave={afterExamSave}
                    visibleSetter={() => setEditExamDialogVisible(false)}
                />
            }

            {writtenTestDialogVisible &&
                <CompetitionJobWrittenExamEditDialog
                    value={calendarChange}
                    competition={competition}
                    visible={writtenTestDialogVisible}
                    onUploadSuccess={onFileUploadSuccess}
                    onUploadError={onFileUploadError}
                    visibleSetter={() => setWrittenTestDialogVisible(false)}
                />
            }

            {writtenTestApproveDialogVisible &&
                <CompetitionJobWrittenExamApproveDialog
                    value={calendarChange}
                    competition={competition}
                    visible={writtenTestApproveDialogVisible}
                    visibleSetter={() => setWrittenTestApproveDialogVisible(false)}
                    afterSave={afterChangeApprove}
                />
            }

            {writtenTestHistoryDialogVisible &&
                <CompetitionJobWrittenExamChangeHistoryDialog
                    competition={competition}
                    visible={writtenTestHistoryDialogVisible}
                    visibleSetter={() => setWrittenTestHistoryDialogVisible(false)}
                />
            }

            {showValidationErrorDialog &&
                <ValidationErrorModalComponent
                    visible={showValidationErrorDialog}
                    errors={validationErrors}
                    closeDialog={() => setShowValidationErrorDialog(false)}
                />
            }

            {deleteExamDialogVisible &&
                <DeleteModalComponent
                    visible={deleteExamDialogVisible}
                    item={selectedExam ? CompetitionUtils.getExamNameByType(selectedExam) : ''}
                    closeDialog={() => {
                        setDeleteExamDialogVisible(false);
                    }}
                    deleteRecord={() => deleteExam()}
                />
            }
        </>
    );
};

export default CompetitionJobCalendarComponent;
